import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useField } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';

import FormikRawInput from './FormikRawInput';
import useInput from '../hooks/useInput';

export interface FormikInputProps {
  id?: string;
  label?: string;
  information?: string;
  name: string;
  placeholder?: string;
  type?: string;
  classInput?: string;
  inputProps?: object;
  icon?: string;
  swap?: boolean;
  iconProps?: object;
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  transformer?: (value: string) => string;
  debounceTime?: number;
}

export function FormikInput({
  id: defaultId,
  label,
  information,
  name,
  placeholder,
  type = 'text',
  classInput,
  inputProps = {},
  icon,
  swap = false,
  iconProps = {},
  className,
  children,
  disabled,
  onChange = () => false,
  onBlur = () => false,
  onFocus = () => false,
  transformer = (value) => value,
  debounceTime = 500
}:FormikInputProps) {
  const [field, meta, helper] = useField(name);
  const {
    id, idIcon, idFeedback, ariaDescribedby
  } = useInput(defaultId, label, icon);

  const [isFocused, setIsFocused] = useState(false);
  const [lastValue, setLastValue] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = useCallback(
    debounce((value) => {
      if (!isFocused && lastValue !== value) {
        const newValue = transformer(value);
        setLastValue(newValue);
        if (newValue !== value) {
          helper.setValue(newValue);
        }
        onChange(newValue);
      }
    }, debounceTime),
    [isFocused, lastValue]
  );
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    onFocus();
  }, [onFocus]);
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onBlur();
  }, [onBlur]);

  useEffect(() => {
    if (!meta.error && meta.value) {
      handleChange(meta.value);
    }
  }, [meta.value, meta.error, handleChange]);
  return (
    <div className={classNames(className)}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
        </label>
      )}
      <FormikRawInput
        id={id}
        placeholder={placeholder}
        ariaDescribedby={ariaDescribedby}
        type={type}
        classInput={classInput}
        inputProps={inputProps}
        icon={icon}
        idIcon={idIcon}
        swap={swap}
        iconProps={iconProps}
        idFeedback={idFeedback}
        disabled={disabled}
        meta={meta}
        {...field}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
      {information && (
        <small className="text-muted form-text text-muted">
          {information}
        </small>
      )}
      {children}
    </div>
  );
}

FormikInput.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  information: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  classInput: PropTypes.string,
  inputProps: PropTypes.object,
  icon: PropTypes.string,
  swap: PropTypes.bool,
  iconProps: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  transformer: PropTypes.func,
  debounceTime: PropTypes.number
};

export default FormikInput;
